import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, Users, CheckCircle, XCircle, Clock, Activity } from "lucide-react";

export default function AdminAppealAnalytics() {
  // Fetch appeal statistics
  const { data: stats, isLoading: statsLoading } = (trpc.admin as any).appealAnalytics.getAppealStats.useQuery();

  // Fetch appeal trends
  const { data: trends } = (trpc.admin as any).appealAnalytics.getAppealTrends.useQuery({
    period: "daily",
    days: 30,
  });

  // Fetch approval rates
  const { data: approvalRates } = (trpc.admin as any).appealAnalytics.getApprovalRates.useQuery();

  // Fetch resolution time distribution
  const { data: resolutionDistribution } = (trpc.admin as any).appealAnalytics.getResolutionTimeDistribution.useQuery();

  // Fetch outcome analysis
  const { data: outcomes } = (trpc.admin as any).appealAnalytics.getOutcomeAnalysis.useQuery();

  // Fetch top appeal reasons
  const { data: topReasons } = (trpc.admin as any).appealAnalytics.getTopAppealReasons.useQuery();

  // Fetch reviewer stats
  const { data: reviewerStats } = (trpc.admin as any).appealAnalytics.getReviewerStats.useQuery();

  const COLORS = ["#10b981", "#ef4444", "#f59e0b", "#6b7280"];

  return (
    <div className="min-h-screen bg-background">
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl mb-2">Appeal Analytics</h1>
          <p className="text-muted-foreground">Comprehensive insights into user appeals and suspension outcomes</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users size={16} /> Total Appeals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.totalAppeals || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">{stats?.pendingAppeals || 0} pending review</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" /> Approval Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{Math.round(stats?.approvalRate || 0)}%</div>
              <p className="text-xs text-muted-foreground mt-1">{stats?.approvedAppeals || 0} approved</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock size={16} className="text-blue-600" /> Avg Resolution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.avgResolutionTime || 0}d</div>
              <p className="text-xs text-muted-foreground mt-1">days to resolve</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <XCircle size={16} className="text-red-600" /> Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.deniedAppeals || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">{Math.round(((stats?.deniedAppeals || 0) / (stats?.totalAppeals || 1)) * 100)}% of total</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="trends" className="space-y-4">
          <TabsList>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="outcomes">Outcomes</TabsTrigger>
            <TabsTrigger value="reasons">Reasons</TabsTrigger>
            <TabsTrigger value="resolution">Resolution Time</TabsTrigger>
            <TabsTrigger value="reviewers">Reviewers</TabsTrigger>
          </TabsList>

          {/* Trends Tab */}
          <TabsContent value="trends">
            <Card>
              <CardHeader>
                <CardTitle>Appeal Submission Trends (30 Days)</CardTitle>
                <CardDescription>Daily appeal submissions over the last month</CardDescription>
              </CardHeader>
              <CardContent>
                {trends && trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="#3b82f6" name="Appeals" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Outcomes Tab */}
          <TabsContent value="outcomes">
            <Card>
              <CardHeader>
                <CardTitle>Appeal Outcomes Distribution</CardTitle>
                <CardDescription>Breakdown of all appeal decisions</CardDescription>
              </CardHeader>
              <CardContent>
                {outcomes ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Approved", value: outcomes.approved },
                            { name: "Denied", value: outcomes.denied },
                            { name: "Withdrawn", value: outcomes.withdrawn },
                            { name: "Pending", value: outcomes.pending },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Approved</p>
                        <p className="text-2xl font-bold text-green-600">{outcomes.approved} ({Math.round(outcomes.approvedPercent)}%)</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Denied</p>
                        <p className="text-2xl font-bold text-red-600">{outcomes.denied} ({Math.round(outcomes.deniedPercent)}%)</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Withdrawn</p>
                        <p className="text-2xl font-bold text-yellow-600">{outcomes.withdrawn} ({Math.round(outcomes.withdrawnPercent)}%)</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Pending</p>
                        <p className="text-2xl font-bold text-blue-600">{outcomes.pending} ({Math.round(outcomes.pendingPercent)}%)</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reasons Tab */}
          <TabsContent value="reasons">
            <Card>
              <CardHeader>
                <CardTitle>Top Appeal Reasons</CardTitle>
                <CardDescription>Most common reasons for appeals</CardDescription>
              </CardHeader>
              <CardContent>
                {topReasons && topReasons.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topReasons}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="reason" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#3b82f6" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resolution Time Tab */}
          <TabsContent value="resolution">
            <Card>
              <CardHeader>
                <CardTitle>Resolution Time Distribution</CardTitle>
                <CardDescription>How long it takes to resolve appeals</CardDescription>
              </CardHeader>
              <CardContent>
                {resolutionDistribution ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { name: "< 3 days", value: resolutionDistribution.lessThan3Days },
                        { name: "3-7 days", value: resolutionDistribution.days3To7 },
                        { name: "7-14 days", value: resolutionDistribution.days7To14 },
                        { name: "14-30 days", value: resolutionDistribution.days14To30 },
                        { name: "> 30 days", value: resolutionDistribution.moreThan30Days },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#10b981" name="Appeals" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviewers Tab */}
          <TabsContent value="reviewers">
            <Card>
              <CardHeader>
                <CardTitle>Reviewer Performance</CardTitle>
                <CardDescription>Statistics for each admin reviewer</CardDescription>
              </CardHeader>
              <CardContent>
                {reviewerStats && reviewerStats.length > 0 ? (
                  <div className="space-y-4">
                    {reviewerStats.map((reviewer: any) => (
                      <div key={reviewer.reviewer} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{reviewer.reviewer}</p>
                            <p className="text-sm text-muted-foreground">{reviewer.reviewed} appeals reviewed</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold">{Math.round(reviewer.approvalRate)}%</p>
                            <p className="text-xs text-muted-foreground">approval rate</p>
                          </div>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Approved: </span>
                            <span className="font-medium text-green-600">{reviewer.approved}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Denied: </span>
                            <span className="font-medium text-red-600">{reviewer.denied}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">No reviewer data available</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Approval Rates by Suspension Reason */}
        {approvalRates && approvalRates.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Approval Rates by Suspension Reason</CardTitle>
              <CardDescription>How often appeals are approved for each suspension reason</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {approvalRates.map((reason: any) => (
                  <div key={reason.reason} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium capitalize">{reason.reason}</p>
                        <p className="text-sm text-muted-foreground">{reason.total} total appeals</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{Math.round(reason.approvalRate)}%</p>
                        <p className="text-xs text-muted-foreground">approval rate</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Approved: </span>
                        <span className="font-medium text-green-600">{reason.approved}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Denied: </span>
                        <span className="font-medium text-red-600">{reason.denied}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
